
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, ChevronDownIcon, FilterIcon, SearchIcon, SlidersHorizontal } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

// Sample data for orders
const orders = [
  { 
    id: "ORD-2025-0001", 
    customerName: "Ahmad Bin Abdullah", 
    orderDate: "2025-05-20 09:15 AM", 
    items: 12,
    amount: 245.90, 
    status: "Pending", 
    channel: "Online",
    paymentMethod: "Credit Card" 
  },
  { 
    id: "ORD-2025-0002", 
    customerName: "Sarah Johnson", 
    orderDate: "2025-05-20 10:30 AM", 
    items: 5,
    amount: 78.50, 
    status: "Processing", 
    channel: "In-store",
    paymentMethod: "Cash" 
  },
  { 
    id: "ORD-2025-0003", 
    customerName: "Tan Mei Ling", 
    orderDate: "2025-05-19 03:45 PM", 
    items: 8,
    amount: 156.75, 
    status: "Completed", 
    channel: "Online",
    paymentMethod: "E-wallet" 
  },
  { 
    id: "ORD-2025-0004", 
    customerName: "Kumar Patel", 
    orderDate: "2025-05-19 11:20 AM", 
    items: 3,
    amount: 45.20, 
    status: "Cancelled", 
    channel: "Online",
    paymentMethod: "Bank Transfer" 
  },
  { 
    id: "ORD-2025-0005", 
    customerName: "Nurul Huda", 
    orderDate: "2025-05-18 02:10 PM", 
    items: 10,
    amount: 189.30, 
    status: "Completed", 
    channel: "In-store",
    paymentMethod: "Cash" 
  },
  { 
    id: "ORD-2025-0006", 
    customerName: "David Wong", 
    orderDate: "2025-05-18 09:45 AM", 
    items: 6,
    amount: 112.80, 
    status: "Processing", 
    channel: "Online",
    paymentMethod: "Credit Card" 
  },
  { 
    id: "ORD-2025-0007", 
    customerName: "Siti Aminah", 
    orderDate: "2025-05-17 04:30 PM", 
    items: 9,
    amount: 204.65, 
    status: "Pending", 
    channel: "In-store",
    paymentMethod: "Debit Card" 
  },
  { 
    id: "ORD-2025-0008", 
    customerName: "Lee Chong Wei", 
    orderDate: "2025-05-17 01:15 PM", 
    items: 4,
    amount: 67.90, 
    status: "Completed", 
    channel: "Online",
    paymentMethod: "E-wallet" 
  },
];

const ViewAllOrders = () => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [activeTab, setActiveTab] = useState("all-orders");

  const filteredOrders = orders.filter((order) => {
    // Apply status filter
    if (statusFilter !== "all" && order.status.toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    
    // Apply channel filter
    if (channelFilter !== "all" && order.channel.toLowerCase() !== channelFilter.toLowerCase()) {
      return false;
    }
    
    // Apply search
    if (searchText && !Object.values(order).some(
      value => String(value).toLowerCase().includes(searchText.toLowerCase())
    )) {
      return false;
    }
    
    return true;
  });

  // Helper to get badge color based on order status
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200">Pending</Badge>;
      case "processing":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">Processing</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200">Completed</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-red-50 text-red-700 hover:bg-red-100 border-red-200">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  // Helper to get badge for order channel
  const getChannelBadge = (channel: string) => {
    switch (channel.toLowerCase()) {
      case "online":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200">Online</Badge>;
      case "in-store":
        return <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200">In-store</Badge>;
      default:
        return <Badge variant="outline">{channel}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">All Orders</h1>
      
      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all-orders">All Orders</TabsTrigger>
          <TabsTrigger value="online">Online Orders</TabsTrigger>
          <TabsTrigger value="in-store">In-Store Orders</TabsTrigger>
        </TabsList>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  className="pl-8 w-full md:w-auto"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="in-store">In-store</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button variant="outline" size="icon">
                  <CalendarIcon className="h-4 w-4" />
                </Button>
                
                <Button variant="outline" size="icon">
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="rounded-md border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left font-medium">Order ID</th>
                      <th className="px-4 py-3 text-left font-medium">Customer</th>
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-left font-medium">Items</th>
                      <th className="px-4 py-3 text-left font-medium">Amount</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Channel</th>
                      <th className="px-4 py-3 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3 font-medium">{order.id}</td>
                        <td className="px-4 py-3">{order.customerName}</td>
                        <td className="px-4 py-3">{order.orderDate}</td>
                        <td className="px-4 py-3">{order.items}</td>
                        <td className="px-4 py-3">RM {order.amount.toFixed(2)}</td>
                        <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                        <td className="px-4 py-3">{getChannelBadge(order.channel)}</td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm">View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredOrders.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  No orders found matching your filters.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default ViewAllOrders;
